// Sidebar do Assistente IA no Modo Estudo. Espelhada à QuestionListSidebar
// — fica no lado oposto da lista pra coexistir sem conflito.
//
// Estado:
//   - aberta/fechada controlada por uiPrefs.aiAssistantOpen
//   - histórico de chat por questão (chave = question.id)
//   - reseta a conversa quando a questão atual muda (sinal claro de
//     "nova conversa, contexto novo")
//   - persona detectada pelo backend, renderizada como badge no header
//
// BYOK: quando o aluno preenche apiKey nas prefs, vai no payload da chamada.
// Sem isso o backend usa a chave servidor (Gemini free).
//
// Markdown: por enquanto exibe texto plano com quebras de linha; um upgrade
// futuro pode renderizar markdown (KaTeX, listas, code blocks).

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  ArrowLeftRight,
  Atom,
  Baby,
  BarChart3,
  BookOpenText,
  Brain,
  Check,
  ChevronDown,
  Code2,
  Copy,
  FlaskConical,
  ArrowRight,
  Globe,
  GraduationCap,
  HeartPulse,
  Key,
  Languages,
  Leaf,
  Loader2,
  Network,
  PictureInPicture2,
  Scale,
  ScrollText,
  Send,
  Settings2,
  Sigma,
  Sparkles,
  StickyNote,
  Stethoscope,
  Users,
  X
} from "lucide-react";
import { CadernoNotesPanel } from "./CadernoNotesPanel";
import { useNotesWindowOpen } from "./NotesWindow";
import { AnimatedTabs } from "@app/ui/components/animated-tabs";
import { flagEnabled } from "@app/ui/lib/feature-flags";
import { Switch } from "@app/ui/components/switch";
import { api, ApiError, type AiChatTurn, type AiPersona, type QuestaoStudy } from "./api";
import { friendlyAiError, type FriendlyAiError } from "./friendlyAiError";
import { data, studentUiVisibility } from "./App";
import {
  uiPrefs,
  toggleAiAssistantOpen,
  toggleQuestionListSide,
  setAiByok,
  setAiAccessMode,
  setAiAssistantEnabled,
  setAiAssistantWidth,
  setAiModel,
  setAiModelPreset,
  setAiProvider,
  computeAiMaxWidth,
  AI_WIDTH_MIN
} from "./uiPrefs";
import { useProviderCatalog } from "./aiCatalog";
import { AiOnboarding } from "./AiOnboarding";
import { ensureKaTeX, renderMathIn } from "./StudyWorkspace";
import {
  aiAssistantPrefs,
  verbosityLabel,
  verbosityDescription,
  type AssistantVerbosity
} from "./aiPrefs";

interface ChatHistoryByQuestion {
  [questionId: string]: AiChatTurn[];
}

interface AiRuntimeMeta {
  providerUsed: string;
  modelUsed?: string;
  modelKindUsed?: "general" | "reasoning" | "coding";
  fallbackUsed?: boolean;
  fallbackReason?: string;
}

export const chatHistory = signal<ChatHistoryByQuestion>({});
export const personaByQuestion = signal<Record<string, AiPersona>>({});

/** Override de verbosity por sessão (não persiste). Null = usa a pref do user.
 *  Compartilhado entre AiAssistant e AiExplainBlock — quando o aluno troca
 *  num dos lugares, vale no outro. Reseta a cada page reload. */
export const sessionVerbosityOverride = signal<AssistantVerbosity | null>(null);

export function activeVerbosity(): AssistantVerbosity {
  return sessionVerbosityOverride.value ?? aiAssistantPrefs.value.verbosity;
}

// ===== Pop-out do AiAssistant ==============================================
// Mesma mecânica do NotesWindow: a página principal abre `?view=ai-window&bankId=X`
// em janela nova; o popup escuta o canal "ai-assistant-window" pra receber o
// contexto da questão atual e troca conforme o aluno navega no bloco principal.
// O bloco inline mostra um banner "trazer para cá" quando detecta o popup vivo.
const AI_WINDOW_HEARTBEAT_MS = 5_000;
const AI_WINDOW_STORAGE_PREFIX = "ai-assistant-window-active:";
const AI_WINDOW_CHANNEL = "ai-assistant-window";
const AI_WINDOW_CONTEXT_CHANNEL = "ai-assistant-window-context";

export interface AiAssistantWindowContext {
  bankId: string;
  question: QuestaoStudy | null;
  respostaAluno: number | null;
}

/** Posta o contexto atual (questão + resposta) pra o popup. Best-effort. */
export function broadcastAiAssistantContext(ctx: AiAssistantWindowContext): void {
  try {
    const bc = new BroadcastChannel(AI_WINDOW_CONTEXT_CHANNEL);
    bc.postMessage(ctx);
    bc.close();
  } catch {
    /* sem BroadcastChannel — popup vai pedir contexto pelo storage poll */
  }
  // Fallback localStorage pra navegadores sem BroadcastChannel: o popup
  // lê esse blob no mount e em polling de 1s.
  try {
    localStorage.setItem(
      `ai-assistant-window-ctx:${ctx.bankId}`,
      JSON.stringify({ ...ctx, t: Date.now() })
    );
  } catch {
    /* ignore quota */
  }
}

function aiWindowBroadcast(
  event: "open" | "close" | "heartbeat",
  bankId: string
) {
  try {
    const bc = new BroadcastChannel(AI_WINDOW_CHANNEL);
    bc.postMessage({ event, bankId, t: Date.now() });
    bc.close();
  } catch {
    /* sem BroadcastChannel */
  }
}

/** Sinaliza pro main que o popup tá vivo. Chamado de dentro do popup. */
export function aiAssistantWindowHeartbeat(bankId: string) {
  aiWindowBroadcast("heartbeat", bankId);
  try {
    localStorage.setItem(`${AI_WINDOW_STORAGE_PREFIX}${bankId}`, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function aiAssistantWindowOpen(bankId: string) {
  aiWindowBroadcast("open", bankId);
  try {
    localStorage.setItem(`${AI_WINDOW_STORAGE_PREFIX}${bankId}`, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function aiAssistantWindowClose(bankId: string) {
  aiWindowBroadcast("close", bankId);
  try {
    localStorage.removeItem(`${AI_WINDOW_STORAGE_PREFIX}${bankId}`);
  } catch {
    /* ignore */
  }
}

/** Abre o popup (idempotente — mesmo `name` foca janela existente). */
export function openAiAssistantWindow(bankId: string): void {
  const url = `/student/?view=ai-window&bankId=${encodeURIComponent(bankId)}`;
  window.open(url, `ai-assistant-${bankId}`, "width=560,height=820,resizable=yes,scrollbars=yes");
}

/** Hook usado pelo bloco inline pra saber se EXISTE popup ativo. */
export function useAiAssistantWindowOpen(bankId: string | null): boolean {
  const [open, setOpen] = useState<boolean>(false);
  useEffect(() => {
    if (!bankId) {
      setOpen(false);
      return;
    }
    let aliveTimeout: ReturnType<typeof setTimeout> | null = null;
    function markAlive() {
      setOpen(true);
      if (aliveTimeout) clearTimeout(aliveTimeout);
      aliveTimeout = setTimeout(
        () => setOpen(false),
        AI_WINDOW_HEARTBEAT_MS * 2 + 1000
      );
    }
    function markDead() {
      if (aliveTimeout) clearTimeout(aliveTimeout);
      setOpen(false);
    }
    try {
      const ts = parseInt(
        localStorage.getItem(`${AI_WINDOW_STORAGE_PREFIX}${bankId}`) ?? "0",
        10
      );
      if (ts && Date.now() - ts < AI_WINDOW_HEARTBEAT_MS * 3) markAlive();
    } catch {
      /* ignore */
    }
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(AI_WINDOW_CHANNEL);
      bc.onmessage = (ev) => {
        const data = ev.data as { event?: string; bankId?: string };
        if (data?.bankId !== bankId) return;
        if (data.event === "open" || data.event === "heartbeat") markAlive();
        else if (data.event === "close") markDead();
      };
    } catch {
      /* ignore */
    }
    const poll = window.setInterval(() => {
      try {
        const ts = parseInt(
          localStorage.getItem(`${AI_WINDOW_STORAGE_PREFIX}${bankId}`) ?? "0",
          10
        );
        if (ts && Date.now() - ts < AI_WINDOW_HEARTBEAT_MS * 3) markAlive();
        else markDead();
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => {
      window.clearInterval(poll);
      if (bc) bc.close();
      if (aliveTimeout) clearTimeout(aliveTimeout);
    };
  }, [bankId]);
  return open;
}

/** Canal de contexto exposto pra o popup escutar. Não usado pelo main. */
export { AI_WINDOW_CONTEXT_CHANNEL };
// ===== fim Pop-out =========================================================

/** Tab ativa no painel direito do Modo Estudo: "ai" mostra o tutor, "notes"
 *  mostra anotações do banco. Persiste em localStorage pra lembrar a
 *  preferência entre sessões. */
export type StudyRightPanel = "ai" | "notes";
function readStudyRightPanel(): StudyRightPanel {
  if (typeof window === "undefined") return "ai";
  const v = localStorage.getItem("study.right-panel");
  return v === "notes" ? "notes" : "ai";
}
export const studyRightPanel = signal<StudyRightPanel>(readStudyRightPanel());
export function setStudyRightPanel(v: StudyRightPanel): void {
  studyRightPanel.value = v;
  try {
    localStorage.setItem("study.right-panel", v);
  } catch {
    /* ignore */
  }
}

/** Hidrata `chatHistory` e `personaByQuestion` com dados vindos do
 *  backend (bank_progress.data.aiChats). Chamado pelo StudyWorkspace ao
 *  carregar o banco. */
export function hydrateChatHistoryFromBank(
  aiChats:
    | Record<
        string,
        { history: Array<{ role: "user" | "assistant"; content: string }>; persona: AiPersona | null }
      >
    | undefined
): void {
  if (!aiChats) return;
  const nextHistory: ChatHistoryByQuestion = { ...chatHistory.value };
  const nextPersona: Record<string, AiPersona> = { ...personaByQuestion.value };
  for (const [qid, thread] of Object.entries(aiChats)) {
    // Conversa do servidor sobrescreve só se não há conversa local mais
    // recente (evita race quando aluno está enviando msg enquanto load
    // termina). Heurística simples: se já existem turns localmente, mantém.
    if (!nextHistory[qid] || nextHistory[qid].length === 0) {
      nextHistory[qid] = thread.history;
    }
    if (thread.persona && !nextPersona[qid]) {
      nextPersona[qid] = thread.persona;
    }
  }
  chatHistory.value = nextHistory;
  personaByQuestion.value = nextPersona;
}
const metaByQuestion = signal<Record<string, AiRuntimeMeta>>({});
const settingsOpen = signal<boolean>(false);
const onboardingOpen = signal<boolean>(false);

const PERSONA_ICONS: Record<string, any> = {
  HeartPulse,
  Baby,
  Brain,
  Stethoscope,
  Scale,
  Sigma,
  Atom,
  FlaskConical,
  Leaf,
  BarChart3,
  ScrollText,
  Globe,
  Users,
  BookOpenText,
  Languages,
  Code2,
  Network,
  GraduationCap
};

const PERSONA_COLOR_CLASS: Record<AiPersona["color"], string> = {
  emerald: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300",
  sky: "bg-sky-500/15 text-sky-700 ring-sky-500/30 dark:text-sky-300",
  violet: "bg-violet-500/15 text-violet-700 ring-violet-500/30 dark:text-violet-300",
  amber: "bg-amber-500/15 text-amber-700 ring-amber-500/30 dark:text-amber-300",
  rose: "bg-rose-500/15 text-rose-700 ring-rose-500/30 dark:text-rose-300",
  teal: "bg-teal-500/15 text-teal-700 ring-teal-500/30 dark:text-teal-300",
  blue: "bg-blue-500/15 text-blue-700 ring-blue-500/30 dark:text-blue-300",
  indigo: "bg-indigo-500/15 text-indigo-700 ring-indigo-500/30 dark:text-indigo-300"
};

interface AiAssistantProps {
  question: QuestaoStudy | null;
  respostaAluno: number | null;
  /** Bank em estudo no momento — usado pra persistir o histórico do tutor
   *  por questão em bank_progress.data.aiChats[qid] e pra limpar conversa. */
  bankId?: string | null;
}

export function AiAssistant({ question, respostaAluno, bankId }: AiAssistantProps) {
  const enabled = uiPrefs.value.aiAssistantEnabled;
  const open = uiPrefs.value.aiAssistantOpen;
  const side = uiPrefs.value.questionListSide;
  const width = uiPrefs.value.aiAssistantWidth;
  const [dragging, setDragging] = useState(false);
  // Tab ativa: "ai" (tutor IA) ou "notes" (anotações do banco). Pedido
  // 2026-05-19: no Modo Estudo, anotações usam o MESMO espaço da IA com
  // toggle entre os dois. [[project-notes-in-study-mode-toggle]]
  const tabActive = studyRightPanel.value;
  // Sincroniza com pop-up dedicado: quando aluno abriu a janela separada
  // de anotações, a tab inline fica desabilitada.
  const notesPopupOpen = useNotesWindowOpen(bankId ?? null);
  // Mesma ideia pra o popup do tutor IA: quando aberto, troca o body
  // inline por um banner "trazer para cá".
  const aiPopupOpen = useAiAssistantWindowOpen(bankId ?? null);
  // Se o popup está aberto e a tab atual é "notes", força "ai" pra não
  // mostrar o painel duplicado.
  const effectiveTab: StudyRightPanel =
    tabActive === "notes" && notesPopupOpen ? "ai" : tabActive;

  // Broadcast do contexto (questão + respostaAluno) pro popup. Roda em todo
  // render — postMessage é barato e o popup só aplica quando muda. Isso é
  // o que garante "ao trocar a questão, troca o contexto da janela popup".
  useEffect(() => {
    if (!bankId) return;
    broadcastAiAssistantContext({
      bankId,
      question: question ?? null,
      respostaAluno
    });
  }, [bankId, question?.id, respostaAluno]);

  if (!enabled) return null;

  // Quando lista=right (flex-row-reverse), AI está à ESQUERDA na tela
  // → handle de resize fica na BORDA DIREITA (lado do conteúdo).
  // Quando lista=left (flex-row), AI à direita → handle na esquerda.
  const handleOnRight = side === "right";

  // Drag: amplia/reduz a largura ao arrastar lateralmente. Clamp dinâmico
  // baseado no espaço livre da viewport (computeAiMaxWidth).
  function startDrag(e: MouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = uiPrefs.value.aiAssistantWidth;
    setDragging(true);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";

    function onMove(ev: MouseEvent) {
      const delta = handleOnRight ? ev.clientX - startX : startX - ev.clientX;
      const maxW = computeAiMaxWidth({
        appSidebarCollapsed: false,
        questionListCollapsed: uiPrefs.value.questionListCollapsed
      });
      const next = Math.max(AI_WIDTH_MIN, Math.min(maxW, startW + delta));
      setAiAssistantWidth(next);
    }
    function onUp() {
      setDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  // overflow-hidden no <aside> + handle ABSOLUTO em cima — fora do flex
  // pra cursor ficar correto e não afetar o layout interno.
  const baseClass =
    "relative hidden overflow-hidden rounded-md border bg-card/50 lg:flex lg:shrink-0 lg:flex-col lg:sticky lg:top-32";
  // Não animamos `width` quando o user está arrastando, pra reagir
  // instantâneo. Só animamos abertura/fechamento.
  const transitionClass = dragging
    ? ""
    : "transition-[width,opacity,margin,border-width] duration-300 ease-out";
  const stateClass = open
    ? "lg:opacity-100 border-border lg:h-[calc(100vh-21rem)]"
    : "lg:w-0 lg:opacity-0 lg:border-0 lg:pointer-events-none";

  const inlineStyle = open
    ? { width: `${width}px`, minWidth: `${AI_WIDTH_MIN}px` }
    : undefined;

  return (
    <aside
      class={`${baseClass} ${transitionClass} ${stateClass}`}
      style={inlineStyle}
    >
      {open && (
        <>
          <RightPanelTabs
            bankId={bankId ?? null}
            active={effectiveTab}
            popupOpen={notesPopupOpen}
            aiPopupOpen={aiPopupOpen}
          />
          {effectiveTab === "ai" ? (
            aiPopupOpen ? (
              <AiPopupBannerInline bankId={bankId ?? null} />
            ) : (
              <AiAssistantBody
                question={question}
                respostaAluno={respostaAluno}
                bankId={bankId ?? null}
                side={side}
              />
            )
          ) : (
            <div class="min-h-0 flex-1">
              {bankId ? (
                <CadernoNotesPanel targetKind="bank" targetId={bankId} />
              ) : (
                <div class="flex h-full items-center justify-center px-4 text-center text-xs text-muted-foreground">
                  Carregando banco…
                </div>
              )}
            </div>
          )}
          {/* Handle de resize — barra fininha clicável na borda interna. */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Redimensionar sidebar de IA"
            onMouseDown={startDrag}
            class={`absolute top-0 z-20 h-full w-1.5 cursor-ew-resize bg-transparent transition-colors hover:bg-primary/40 ${
              handleOnRight ? "right-0" : "left-0"
            } ${dragging ? "bg-primary/60" : ""}`}
          />
        </>
      )}
    </aside>
  );
}

/** Tabs no topo do painel direito do Modo Estudo: alterna entre Tutor IA
 *  e Anotações do banco. Inclui botão "abrir em janela" pra estourar a
 *  anotação num pop-up dedicado. */
function RightPanelTabs({
  bankId,
  active,
  popupOpen,
  aiPopupOpen
}: {
  bankId: string | null;
  active: StudyRightPanel;
  popupOpen: boolean;
  aiPopupOpen: boolean;
}) {
  function openInWindow() {
    if (!bankId) return;
    // URL dedicada com ?view=notes-window — App.tsx detecta no mount
    // antes de qualquer bootstrap e renderiza apenas o NotesWindow.
    // Sem isso, o pop-up caía na home. [[project-notes-in-study-mode-toggle]]
    const url = `/student/?view=notes-window&bankId=${encodeURIComponent(bankId)}`;
    window.open(url, `notes-${bankId}`, "width=560,height=820,resizable=yes,scrollbars=yes");
    // Quando o pop-up sobe, força a tab inline pra "ai" pra evitar dois
    // editores simultâneos.
    setStudyRightPanel("ai");
  }
  function openAiInWindow() {
    if (!bankId) return;
    openAiAssistantWindow(bankId);
  }
  // Quando o pop-up está aberto, a tab Anotações fica DESABILITADA pra
  // evitar edição simultânea + corrida de auto-save. Pedido 2026-05-19.
  const notesDisabled = popupOpen;
  return (
    <div class="flex shrink-0 items-center gap-1 border-b border-border bg-muted/30 px-2 py-1">
      <button
        type="button"
        onClick={() => setStudyRightPanel("ai")}
        title="Tutor IA"
        aria-pressed={active === "ai"}
        class={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold transition-colors ${
          active === "ai"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`}
      >
        <Sparkles className="size-3" />
        Tutor IA
      </button>
      <button
        type="button"
        onClick={() => !notesDisabled && setStudyRightPanel("notes")}
        disabled={notesDisabled}
        title={
          notesDisabled
            ? "Anotações abertas em janela dedicada · feche-a pra editar aqui"
            : "Minhas anotações deste banco"
        }
        aria-pressed={active === "notes" && !notesDisabled}
        class={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold transition-colors ${
          notesDisabled
            ? "cursor-not-allowed text-muted-foreground/40"
            : active === "notes"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`}
      >
        <StickyNote className="size-3" />
        Anotações
        {notesDisabled && <span class="text-[9px]">· janela aberta</span>}
      </button>
      <div class="ml-auto flex items-center gap-1">
        {/* Trocar lado funciona em AI E Anotações — quem usa o painel direito
            espera o mesmo controle independente da tab. Pedido 2026-05-20. */}
        <button
          type="button"
          onClick={toggleQuestionListSide}
          title="Trocar lado do painel"
          aria-label="Trocar lado"
          class="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <ArrowLeftRight className="size-3.5" />
        </button>
        {active === "notes" && !notesDisabled && (
          <button
            type="button"
            onClick={openInWindow}
            title="Abrir anotações em janela dedicada"
            aria-label="Abrir em janela"
            class="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <PictureInPicture2 className="size-3.5" />
          </button>
        )}
        {active === "ai" && !aiPopupOpen && bankId && (
          <button
            type="button"
            onClick={openAiInWindow}
            title="Abrir tutor IA em janela dedicada"
            aria-label="Abrir tutor em janela"
            class="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <PictureInPicture2 className="size-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export function AiAssistantBody({
  question,
  respostaAluno,
  bankId
}: AiAssistantProps & { side: "left" | "right" }) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<FriendlyAiError | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const qid = question?.id ?? "";
  const history = useMemo(() => chatHistory.value[qid] ?? [], [qid, chatHistory.value]);
  const persona = personaByQuestion.value[qid] ?? null;
  const runtimeMeta = metaByQuestion.value[qid] ?? null;

  // Scroll pro final quando mensagens novas chegam.
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [history.length, sending]);

  // Foca input quando a sidebar abre (mas não no primeiro render — o aluno
  // ainda pode estar lendo a questão).
  useEffect(() => {
    if (!uiPrefs.value.aiAssistantOpen) return;
    // intencional: não auto-foco
  }, []);

  if (!question) {
    return (
      <div class="flex flex-1 items-center justify-center px-6 py-8 text-center text-xs text-muted-foreground">
        Escolha uma questão para conversar com o tutor.
      </div>
    );
  }

  async function send() {
    if (sending) return;
    const msg = draft.trim();
    if (!msg) return;
    setDraft("");
    setError(null);
    setSending(true);

    const turn: AiChatTurn = { role: "user", content: msg };
    const newHistory: AiChatTurn[] = [...history, turn];
    chatHistory.value = { ...chatHistory.value, [qid]: newHistory };

    try {
      const byok =
        uiPrefs.value.aiAccessMode === "byok" &&
        uiPrefs.value.aiByokApiKey.trim().length > 0
          ? {
              provider: uiPrefs.value.aiProvider,
              apiKey: uiPrefs.value.aiByokApiKey.trim(),
              model: uiPrefs.value.aiModel.trim() || undefined
            }
          : undefined;
      const resp = await api.aiChat({
        bankId: bankId ?? undefined,
        question: {
          id: question!.id,
          tema: question!.tema ?? null,
          fonte: question!.fonte ?? null,
          pergunta: question!.pergunta ?? null,
          caso: question!.caso ?? null,
          // QuestaoStudy usa `opcoes: string[]` — fallback pra
          // `alternativas` caso alguma fonte legada mande no nome antigo.
          alternativas:
            (question as any).alternativas ??
            (question as any).opcoes ??
            [],
          correta: (question as any).correta ?? null,
          // Resolução da questão é `explicacao` no schema; backend usa
          // como referência interna no system prompt.
          resolucao:
            (question as any).resolucao ??
            (question as any).explicacao ??
            null,
          anulada: !!(question as any).anulada
        },
        respostaAluno: respostaAluno,
        // Strip do marker invisível do AiExplainBlock antes de mandar pro
        // backend — modelo não precisa ver e seria token desperdiçado.
        history: history.slice(-10).map((t) => ({
          ...t,
          content:
            typeof t.content === "string"
              ? t.content.replace(/^<!--ai-explain-injected-->\s*\n?/, "")
              : t.content
        })),
        message: msg,
        verbosity: activeVerbosity(),
        provider: uiPrefs.value.aiProvider,
        model: uiPrefs.value.aiModel.trim() || undefined,
        modelCatalogId: uiPrefs.value.aiModelCatalogId || undefined,
        accessMode: uiPrefs.value.aiAccessMode,
        byok
      });
      chatHistory.value = {
        ...chatHistory.value,
        [qid]: [...newHistory, { role: "assistant", content: resp.reply }]
      };
      if (resp.persona) {
        personaByQuestion.value = {
          ...personaByQuestion.value,
          [qid]: resp.persona
        };
      }
      metaByQuestion.value = {
        ...metaByQuestion.value,
        [qid]: {
          providerUsed: resp.providerUsed,
          modelUsed: resp.modelUsed,
          modelKindUsed: resp.modelKindUsed,
          fallbackUsed: resp.fallbackUsed,
          fallbackReason: resp.fallbackReason
        }
      };
    } catch (err) {
      // Mapeia código backend → friendly PT-BR + ação sugerida.
      // Decisão 2026-05-25: NÃO promovemos BYOK aos alunos — aluno usa
      // a IA da plataforma sem precisar pensar em chave própria. Se o
      // backend devolver byok_required, o friendlyAiError já transforma
      // em mensagem genérica "tente de novo". Sem auto-abrir onboarding.
      const friendly = friendlyAiError(err);
      setError(friendly);
      // Rollback do turn do usuário pra ele poder reeditar.
      chatHistory.value = {
        ...chatHistory.value,
        [qid]: history
      };
      setDraft(msg);
    } finally {
      setSending(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }

  function clearChat() {
    // Limpa local imediatamente (UX otimista) + apaga no servidor (best-effort).
    chatHistory.value = { ...chatHistory.value, [qid]: [] };
    metaByQuestion.value = Object.fromEntries(
      Object.entries(metaByQuestion.value).filter(([key]) => key !== qid)
    );
    setError(null);
    if (bankId && qid) {
      api.aiChatClear(bankId, qid).catch((e) => {
        console.warn("[ai-chat-clear] falhou (mantido só local):", e);
      });
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <div class="flex h-full min-h-0 flex-col">
      <AiAssistantHeader
        persona={persona}
        runtimeMeta={runtimeMeta}
        onClearChat={clearChat}
      />
      {settingsOpen.value && <AiSettingsPanel onClose={() => (settingsOpen.value = false)} />}
      {onboardingOpen.value && (
        <AiOnboarding
          asModal
          userId={data.value?.user?.id ?? ""}
          onSuccess={() => (onboardingOpen.value = false)}
          onClose={() => (onboardingOpen.value = false)}
        />
      )}
      <div
        ref={listRef}
        class="min-h-0 flex-1 space-y-3 overflow-y-auto scroll-thin px-3 py-3"
      >
        {history.length === 0 && !sending && (
          <AiEmptyState question={question} />
        )}
        {history.map((turn, i) => (
          <ChatBubble key={i} turn={turn} persona={persona} />
        ))}
        {sending && <TypingBubble persona={persona} />}
        {error && (
          <div class="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300">
            <strong class="mb-0.5 block">{error.title}</strong>
            <span class="block leading-relaxed">{error.message}</span>
            {error.action === "retry" && (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  if (draft.trim()) void send();
                }}
                class="mt-1.5 inline-flex items-center rounded border border-red-500/40 bg-background px-2 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-300"
              >
                {error.actionLabel ?? "Tentar de novo"}
              </button>
            )}
            {error.action === "open_byok_settings" && (
              <button
                type="button"
                onClick={() => {
                  onboardingOpen.value = true;
                  setError(null);
                }}
                class="mt-1.5 inline-flex items-center rounded border border-red-500/40 bg-background px-2 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-300"
              >
                {error.actionLabel ?? "Configurar chave"}
              </button>
            )}
          </div>
        )}
      </div>
      <div class="border-t border-border bg-background/50 p-3">
        <div class="flex gap-2">
          <textarea
            ref={inputRef}
            value={draft}
            placeholder="Pergunte sobre essa questão…"
            onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
            onKeyDown={onKeyDown}
            rows={2}
            class="min-h-[44px] flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/60 focus:ring-1 focus:ring-primary/30"
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending || draft.trim().length === 0}
            title="Enviar (Enter)"
            class="grid size-11 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
          </button>
        </div>
        <p class="mt-1.5 text-[10px] leading-tight text-muted-foreground">
          Enter envia · Shift+Enter quebra linha. A IA pode errar — confirme
          informações importantes.
        </p>
      </div>
    </div>
  );
}

function AiAssistantHeader({
  persona,
  runtimeMeta,
  onClearChat
}: {
  persona: AiPersona | null;
  runtimeMeta: AiRuntimeMeta | null;
  onClearChat: () => void;
}) {
  return (
    <header class="flex items-center gap-2 border-b border-border px-3 py-2.5">
      <PersonaBadge persona={persona} />
      <div class="min-w-0 flex-1">
        <h2 class="truncate text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Assistente IA
        </h2>
        {persona && (
          <p class="truncate text-xs font-medium text-foreground">{persona.label}</p>
        )}
        {runtimeMeta && shouldShowRuntimeMeta(runtimeMeta) && (
          <p class="truncate text-[10px] text-muted-foreground">
            {formatRuntimeMetaLabel(runtimeMeta)}
          </p>
        )}
      </div>
      <VerbosityChip />
      <button
        type="button"
        onClick={onClearChat}
        title="Limpar conversa"
        aria-label="Limpar conversa"
        class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Sparkles className="size-4" />
      </button>
      <button
        type="button"
        onClick={() => (settingsOpen.value = !settingsOpen.value)}
        title="Configurações da IA"
        aria-label="Configurações da IA"
        class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Settings2 className="size-4" />
      </button>
      {/* "Trocar lado" agora vive no RightPanelTabs (visível em ambas as
          tabs IA e Anotações), removido daqui pra evitar duplicação. */}
      <button
        type="button"
        onClick={toggleAiAssistantOpen}
        title="Fechar assistente"
        aria-label="Fechar assistente"
        class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </header>
  );
}

/** Chip de verbosity exibido no header do AiAssistant + AiExplainBlock.
 *  Mostra o nível ativo e permite trocar inline (override por sessão).
 *  O override é compartilhado entre os dois componentes via signal global.
 *
 *  Implementação manual (button + popover) em vez de <select> nativo —
 *  o nativo renderiza com estilo do OS que destoa do resto da UI. */
export function VerbosityChip({ size = "sm" }: { size?: "sm" | "xs" } = {}) {
  const active = activeVerbosity();
  const isOverride = sessionVerbosityOverride.value !== null;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const padding = size === "xs" ? "px-1.5 py-0.5" : "px-2 py-1";
  const textSize = size === "xs" ? "text-[10px]" : "text-[11px]";

  const options: Array<{ value: AssistantVerbosity; label: string; desc: string }> = [
    { value: "concise", label: "Conciso", desc: verbosityDescription("concise") },
    { value: "standard", label: "Padrão", desc: verbosityDescription("standard") },
    { value: "detailed", label: "Detalhado", desc: verbosityDescription("detailed") }
  ];

  return (
    <div ref={wrapRef} class="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`Nível de detalhe: ${verbosityLabel(active)}. ${verbosityDescription(active)}${isOverride ? " (override da sessão)" : ""}`}
        aria-label="Nível de detalhe da IA"
        aria-haspopup="listbox"
        class={`inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 ${padding} ${textSize} font-semibold uppercase tracking-wider text-violet-600 outline-none transition-colors hover:bg-violet-500/20 focus:border-violet-500 dark:text-violet-300`}
      >
        {verbosityLabel(active)}
        {!isOverride && <span class="opacity-70">★</span>}
        <ChevronDown className="size-3" />
      </button>
      {open && (
        <div class="absolute right-0 z-50 mt-1 w-60 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg">
          {options.map((opt) => {
            const isActive = active === opt.value;
            const isOverrideHere = sessionVerbosityOverride.value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  sessionVerbosityOverride.value = opt.value;
                  setOpen(false);
                }}
                class={`flex w-full flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-left text-xs transition-colors ${
                  isActive
                    ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                    : "hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                <span class="flex w-full items-center gap-1.5 font-semibold">
                  {opt.label}
                  {isActive && (
                    <span class="ml-auto text-[9px] uppercase tracking-wider opacity-70">
                      {isOverrideHere ? "override" : "pref"}
                    </span>
                  )}
                </span>
                <span class="text-[10px] font-normal leading-snug text-muted-foreground">
                  {opt.desc}
                </span>
              </button>
            );
          })}
          {isOverride && (
            <button
              type="button"
              onClick={() => {
                sessionVerbosityOverride.value = null;
                setOpen(false);
              }}
              class="mt-1 flex w-full items-center justify-center rounded-sm border-t border-border px-2 pt-1.5 text-[10px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Voltar à preferência salva
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PersonaBadge({ persona }: { persona: AiPersona | null }) {
  const Icon =
    persona && PERSONA_ICONS[persona.icon]
      ? PERSONA_ICONS[persona.icon]
      : Sparkles;
  const colorClass = persona
    ? PERSONA_COLOR_CLASS[persona.color]
    : "bg-primary/15 text-primary ring-primary/30";
  return (
    <div
      class={`grid size-9 shrink-0 place-items-center rounded-md ring-1 ${colorClass}`}
      title={persona?.label ?? "Tutor genérico"}
    >
      <Icon className="size-4" />
    </div>
  );
}

function formatRuntimeMeta(meta: AiRuntimeMeta): string {
  const provider = meta.providerUsed
    .replace(/^platform-/, "")
    .replace(/^byok-/, "")
    .replace(/^connected-/, "");
  const source = meta.providerUsed.startsWith("byok-")
    ? "sua chave"
    : meta.providerUsed.startsWith("connected-")
      ? "conta conectada"
      : "plataforma";
  const model = meta.modelUsed ? ` · ${meta.modelUsed}` : "";
  const fallback = meta.fallbackUsed ? " · fallback" : "";
  return `${provider} (${source})${model}${fallback}`;
}

/** Admin pode esconder o rótulo "Gemini · gemini-2.5-flash" do aluno
 *  separadamente quando a IA roda na plataforma vs BYOK do próprio aluno.
 *  [[project-student-ui-visibility]] */
function shouldShowRuntimeMeta(meta: AiRuntimeMeta): boolean {
  const vis = studentUiVisibility.value;
  const isByok = meta.providerUsed.startsWith("byok-");
  return isByok ? vis.aiMetaByok : vis.aiMetaPlatform;
}

function formatRuntimeMetaLabel(meta: AiRuntimeMeta): string {
  const provider = meta.providerUsed
    .replace(/^platform-/, "")
    .replace(/^byok-/, "")
    .replace(/^connected-/, "");
  const source = meta.providerUsed.startsWith("byok-")
    ? "sua chave"
    : meta.providerUsed.startsWith("connected-")
      ? "conta conectada"
      : "plataforma";
  const model = meta.modelUsed ? ` | ${meta.modelUsed}` : "";
  const kind =
    meta.modelKindUsed === "coding"
      ? " | tecnico"
      : meta.modelKindUsed === "reasoning"
        ? " | raciocinio"
        : "";
  const fallback = meta.fallbackUsed ? " | fallback" : "";
  return `${provider} (${source})${model}${kind}${fallback}`;
}

function AiEmptyState({ question }: { question: QuestaoStudy }) {
  const tema = question.tema?.trim() || "essa questão";
  const sugestoes = [
    `Explica a questão de forma simples`,
    `Por que a alternativa correta é a certa?`,
    `Me ensina ${tema} do zero`,
    `Quais são as armadilhas comuns aqui?`
  ];
  // CTA de BYOK só quando: (a) admin habilitou a flag, (b) o aluno ainda não
  // plugou a própria chave, (c) o aluno NÃO fechou o balão via X numa sessão
  // anterior. Pedido 2026-05-23: clicou no X = sumir pra sempre.
  // Flag default = true. Admin pode desabilitar em FeatureFlags →
  // "student.ai.show_byok_cta" pra sumir pra todos os alunos.
  const BYOK_CTA_DISMISSED_KEY = "student.ai.byok_cta_dismissed";
  const byokCtaFlagOn = flagEnabled("student.ai.show_byok_cta", true);
  const [byokDismissed, setByokDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(BYOK_CTA_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  // Decisão 2026-05-25: dentro do CHAT (AiEmptyState), NÃO promover BYOK.
  // Aluno usa a plataforma sem precisar pensar em chave própria. Em outros
  // pontos (settings, onboarding) o CTA fica disponível. Pra reativar aqui,
  // troca pra `byokCtaFlagOn && !byokDismissed && aiAccessMode !== "byok" && (!aiByokApiKey || ...)`.
  // [[feedback 2026-05-25 ("não quero essa mensagem no chat")]]
  const showByokCta = false;
  // Auto-recolhe depois de 4s pra não dominar o painel — vira um chip
  // compacto que continua clicável. O X dispensa permanentemente.
  const [byokExpanded, setByokExpanded] = useState(true);
  useEffect(() => {
    if (!showByokCta) return;
    const t = setTimeout(() => setByokExpanded(false), 4000);
    return () => clearTimeout(t);
  }, [showByokCta]);
  function dismissByokForever() {
    try {
      localStorage.setItem(BYOK_CTA_DISMISSED_KEY, "1");
    } catch {
      /* ignore */
    }
    setByokDismissed(true);
  }
  // Mantém referências vivas mesmo com showByokCta=false, pra não esmagar o
  // ramo de reativação ("dá pra trocar showByokCta=true e funciona"). Tem
  // que vir DEPOIS das declarações: o `void X` antes da `const X` dispara
  // TDZ ReferenceError em runtime (não só warning de tsc). Bug 2026-05-25 —
  // o crash quebrava o Modo Estudo inteiro porque o AiAssistant não montava.
  void byokCtaFlagOn; void byokDismissed; void dismissByokForever; void setByokExpanded;
  return (
    <div class="space-y-3 px-1 py-2">
      <p class="text-xs text-muted-foreground">
        Comece pedindo o que quiser sobre essa questão. O tutor vai responder
        adaptando ao seu nível.
      </p>
      {showByokCta && byokExpanded && (
        <div class="relative rounded-md border border-violet-500/30 bg-violet-500/5 transition-all">
          <button
            type="button"
            onClick={() => (onboardingOpen.value = true)}
            class="block w-full rounded-md px-3 py-2 pr-8 text-left text-[11px] transition-colors hover:bg-violet-500/10"
          >
            <div class="flex items-start gap-2">
              <Key className="mt-0.5 size-3.5 shrink-0 text-violet-600" />
              <div class="min-w-0 flex-1">
                <div class="font-semibold text-violet-700 dark:text-violet-300">
                  Use sua própria chave em 30 segundos
                </div>
                <div class="mt-0.5 text-muted-foreground">
                  Plugue sua chave do Gemini (Free funciona) pra ter cota
                  individual e não competir pelo limite compartilhado.
                </div>
              </div>
              <ArrowRight className="size-3.5 shrink-0 text-violet-600" />
            </div>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              dismissByokForever();
            }}
            title="Não mostrar mais"
            aria-label="Não mostrar este aviso novamente"
            class="absolute right-1.5 top-1.5 grid size-5 place-items-center rounded text-violet-600/70 transition-colors hover:bg-violet-500/15 hover:text-violet-700"
          >
            <X className="size-3" />
          </button>
        </div>
      )}
      {showByokCta && !byokExpanded && (
        <button
          type="button"
          onClick={() => (onboardingOpen.value = true)}
          title="Plugue sua chave própria pra ter cota individual"
          class="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/5 px-2.5 py-1 text-[10px] font-semibold text-violet-700 transition-colors hover:bg-violet-500/10 dark:text-violet-300"
        >
          <Key className="size-3" />
          Use sua chave (mais cota)
        </button>
      )}
      <ul class="space-y-1.5">
        {sugestoes.map((s) => (
          <li key={s}>
            <button
              type="button"
              onClick={() => {
                const ta = document.querySelector<HTMLTextAreaElement>(
                  '[aria-label="message-input"], textarea[placeholder^="Pergunte"]'
                );
                if (ta) {
                  ta.value = s;
                  ta.dispatchEvent(new Event("input", { bubbles: true }));
                  ta.focus();
                }
              }}
              class="w-full rounded-md border border-border bg-background/80 px-3 py-2 text-left text-xs transition-colors hover:bg-accent hover:text-foreground"
            >
              {s}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChatBubble({
  turn,
  persona
}: {
  turn: AiChatTurn;
  persona: AiPersona | null;
}) {
  // Mensagem do aluno fica como texto plano — ele escreveu, não precisa
  // ser interpretada como markdown. Resposta da IA é renderizada via
  // renderBlock (mesmo renderer das questões: markdown estendido +
  // KaTeX + mhchem). Os marcadores que a IA recebe no system prompt
  // batem com o que esse renderer entende.
  if (turn.role === "user") {
    // Balão do aluno: violeta CLARO (tint, não sólido) com texto na cor
    // padrão — contraste muito melhor que o violet+white anterior. Mantém
    // distinção visual da bolha da IA (cinza) mas sem cansar a leitura.
    return (
      <div class="flex justify-end">
        <div class="max-w-[85%] rounded-2xl rounded-tr-sm border border-primary/40 bg-primary/10 px-3.5 py-2.5 text-sm leading-relaxed text-foreground shadow-sm dark:bg-primary/20">
          <pre class="whitespace-pre-wrap break-words font-sans">{turn.content}</pre>
        </div>
      </div>
    );
  }
  return (
    <div class="flex items-start gap-2">
      <PersonaBadge persona={persona} />
      <AssistantBubble content={turn.content} />
    </div>
  );
}

// Renderer completo pra mensagens da IA. Cobre os marcadores que a IA
// recebe no system prompt (formatting-conventions.ts):
//
//   Block: # ## ### títulos, listas - / 1., blockquote >, parágrafos
//   Inline: **bold**, *italic*, `code`, ++sublinhado++, ==marca==, ~~tachado~~
//   Math: $...$ e $$...$$ — escapa pra KaTeX auto-render fazer depois
//
// Estratégia: 1) protege blocos de math substituindo por placeholders;
// 2) escapa HTML; 3) processa marcadores; 4) reinjeta os blocos de math
// originais (KaTeX precisa do texto cru). KaTeX auto-render roda em
// useEffect depois que o HTML está montado.
function renderAiMessage(raw: string): string {
  // 0. Remove marcador interno usado pra detectar explicações injetadas
  //    pelo AiExplainBlock (modo Revisão). Esse marker fica no início do
  //    content pra que ensureExplanationInChatHistory consiga identificar
  //    o turn já injetado e atualizar em vez de duplicar. Pro aluno, é
  //    invisível. Ver ProvaRevisaoView.tsx → EXPLAIN_INJECT_MARK.
  let s = raw.replace(/^<!--ai-explain-injected-->\s*\n?/, "");
  // 1. Protege blocos de math ($$...$$ e $...$) — KaTeX precisa do texto
  //    LATEX original, não escapado.
  const mathBlocks: string[] = [];
  s = s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, body) => {
    mathBlocks.push(`$$${body}$$`);
    return `MATH${mathBlocks.length - 1}`;
  });
  s = s.replace(/\$([^\$\n]+?)\$/g, (_m, body) => {
    mathBlocks.push(`$${body}$`);
    return `MATH${mathBlocks.length - 1}`;
  });

  // 2. Escape HTML do que sobrou.
  s = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  // 3. Block-level: separa em "blocos" por linha em branco, identifica
  //    headers, listas, blockquotes, parágrafos normais.
  const blocks = s.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const html = blocks.map(renderAiBlock).join("\n");

  // 4. Reinjeta math blocks originais (pré-KaTeX).
  return html.replace(/MATH(\d+)/g, (_m, idx) => {
    return mathBlocks[parseInt(idx, 10)] ?? "";
  });
}

function headerClass(level: number): string {
  return [
    "",
    "mb-2 mt-3 text-base font-bold first:mt-0", // h1
    "mb-1.5 mt-3 text-[15px] font-bold first:mt-0", // h2
    "mb-1 mt-2.5 text-sm font-bold first:mt-0", // h3
    "mb-1 mt-2 text-sm font-semibold first:mt-0" // h4
  ][level] ?? "";
}

/** Renderer line-by-line: a IA cospe headers/listas/parágrafos
 *  intercalados dentro do MESMO bloco (sem linhas em branco), e o renderer
 *  anterior por-bloco quebrava nisso ("### título" virava texto literal
 *  quando seguido de linha sem blank line). Aqui fazemos máquina de estado
 *  por linha, fechando/abrindo listas e parágrafos sob demanda. */
function renderAiBlock(block: string): string {
  const lines = block.split("\n");
  const out: string[] = [];
  let listKind: "ul" | "ol" | null = null;
  let paragraphBuf: string[] = [];

  function flushParagraph() {
    if (paragraphBuf.length === 0) return;
    out.push(
      `<p class="my-2 first:mt-0 last:mb-0 leading-relaxed">${paragraphBuf
        .map(renderAiInline)
        .join("<br>")}</p>`
    );
    paragraphBuf = [];
  }
  function flushList() {
    // Fecha tag em construção (paragraphBuf é fechado por flushParagraph).
    if (listKind === null) return;
    const tag = listKind;
    listKind = null;
    // Acha o índice do <ul/ol> aberto no out e fecha.
    out.push(`</${tag}>`);
  }
  function openList(kind: "ul" | "ol") {
    if (listKind === kind) return;
    if (listKind !== null) flushList();
    listKind = kind;
    out.push(
      kind === "ul"
        ? '<ul class="my-1.5 list-disc space-y-1 pl-5">'
        : '<ol class="my-1.5 list-decimal space-y-1 pl-5">'
    );
  }

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (line.trim() === "") {
      flushParagraph();
      flushList();
      continue;
    }

    // Header: # / ## / ### / ####
    const headerMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headerMatch) {
      flushParagraph();
      flushList();
      const level = headerMatch[1].length;
      const text = renderAiInline(headerMatch[2].trim());
      out.push(`<h${level} class="${headerClass(level)}">${text}</h${level}>`);
      continue;
    }

    // Blockquote
    if (/^(&gt;|>)\s+/.test(line)) {
      flushParagraph();
      flushList();
      const inner = renderAiInline(line.replace(/^(&gt;|>)\s?/, ""));
      out.push(
        `<blockquote class="my-2 border-l-2 border-primary/40 pl-3 italic text-foreground/80">${inner}</blockquote>`
      );
      continue;
    }

    // Lista numerada: 1. 2. 3.
    const olMatch = line.match(/^\d+\.\s+(.+)$/);
    if (olMatch) {
      flushParagraph();
      openList("ol");
      out.push(`<li>${renderAiInline(olMatch[1])}</li>`);
      continue;
    }

    // Lista com bullets: - / *
    const ulMatch = line.match(/^[-*]\s+(.+)$/);
    if (ulMatch) {
      flushParagraph();
      openList("ul");
      out.push(`<li>${renderAiInline(ulMatch[1])}</li>`);
      continue;
    }

    // Régua horizontal
    if (/^(?:-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushParagraph();
      flushList();
      out.push('<hr class="my-3 border-border" />');
      continue;
    }

    // Linha de parágrafo — acumula.
    flushList();
    paragraphBuf.push(line);
  }
  flushParagraph();
  flushList();
  return out.join("");
}

function renderAiInline(s: string): string {
  // Ordem importa: code primeiro (não interpretar marcadores dentro), depois
  // negrito (** evita match acidental de *), depois itálico, etc.
  let out = s;
  // Inline code
  out = out.replace(
    /`([^`\n]+)`/g,
    '<code class="rounded bg-accent/60 px-1 py-0.5 font-mono text-[0.88em]">$1</code>'
  );
  // **bold**
  out = out.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  // *italic* / _italic_
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  out = out.replace(/(^|\W)_([^_\n]+)_(?=\W|$)/g, "$1<em>$2</em>");
  // ++sublinhado++
  out = out.replace(
    /\+\+([^+\n]+)\+\+/g,
    '<span class="underline decoration-primary/60 underline-offset-2">$1</span>'
  );
  // ==marca-texto==
  out = out.replace(
    /==([^=\n]+)==/g,
    '<mark class="rounded bg-amber-300/60 px-0.5 text-amber-950 dark:bg-amber-400/40 dark:text-amber-100">$1</mark>'
  );
  // ~~tachado~~
  out = out.replace(/~~([^~\n]+)~~/g, "<s>$1</s>");
  // [link](url) — só http/https pra segurança
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary">$1</a>'
  );
  return out;
}

// Balão da resposta da IA com cinza levemente destacado + botão de
// copiar no canto. O botão fica meio transparente; ao hover do balão
// vira opacidade total.
function AssistantBubble({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // KaTeX render quando o conteúdo muda (resposta nova). ensureKaTeX é
  // idempotente; renderMathIn varre delimitadores ($..$, $$..$$, \(..\),
  // \[..\]) e troca por nodes renderizados.
  useEffect(() => {
    if (!bodyRef.current) return;
    void ensureKaTeX().then(() => {
      if (bodyRef.current) renderMathIn(bodyRef.current);
    });
  }, [content]);

  async function handleCopy() {
    // Strip do marker invisível antes de copiar (ver renderAiMessage step 0).
    const clean = content.replace(/^<!--ai-explain-injected-->\s*\n?/, "");
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clean);
      } else {
        // Fallback p/ navegadores antigos.
        const ta = document.createElement("textarea");
        ta.value = clean;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignora — sem feedback de erro pra não poluir
    }
  }
  return (
    <div class="group relative min-w-0 max-w-[85%] rounded-2xl rounded-tl-sm border border-border bg-muted/60 px-3.5 py-3 pr-9 text-sm shadow-sm dark:bg-muted/30">
      {/* Renderer próprio cobre headers (#), listas, blockquote, sublinhado,
          marca-texto, tachado, links + protege $...$ pro KaTeX auto-render. */}
      <div
        ref={bodyRef}
        class="ai-chat-body text-foreground/95"
        dangerouslySetInnerHTML={{ __html: renderAiMessage(content) }}
      />
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? "Copiado!" : "Copiar resposta"}
        aria-label={copied ? "Resposta copiada" : "Copiar resposta"}
        class={`absolute right-1.5 top-1.5 grid size-7 place-items-center rounded-md text-muted-foreground opacity-60 transition-all hover:bg-accent hover:text-foreground hover:opacity-100 group-hover:opacity-100 ${
          copied ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 opacity-100" : ""
        }`}
      >
        {copied ? <Check className="size-3.5" strokeWidth={2.5} /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}

function TypingBubble({ persona }: { persona: AiPersona | null }) {
  return (
    <div class="flex items-start gap-2">
      <PersonaBadge persona={persona} />
      <div class="flex items-center gap-1 rounded-2xl rounded-tl-sm border border-border bg-background px-3 py-2.5">
        <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" />
        <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" />
        <span class="size-1.5 animate-bounce rounded-full bg-muted-foreground/60" />
      </div>
    </div>
  );
}

function AiSettingsPanel({ onClose }: { onClose: () => void }) {
  const accessMode = uiPrefs.value.aiAccessMode;
  const provider = uiPrefs.value.aiProvider;
  const apiKey = uiPrefs.value.aiByokApiKey;
  const model = uiPrefs.value.aiModel;
  const enabled = uiPrefs.value.aiAssistantEnabled;
  const { providerCatalog } = useProviderCatalog(provider);
  const [hasConnectedGemini, setHasConnectedGemini] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .aiConnections()
      .then((resp) => {
        if (!cancelled) {
          const connected = resp.connections.some(
            (item) => item.provider === "gemini" && item.status === "connected"
          );
          setHasConnectedGemini(connected);
          if (uiPrefs.value.aiAccessMode === "connected" && !connected) {
            setAiAccessMode("platform");
          }
        }
      })
      .catch(() => {
        if (!cancelled) setHasConnectedGemini(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <div class="border-b border-border bg-background/70 px-3 py-3">
      <header class="mb-2 flex items-center justify-between">
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Configurações da IA
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar configurações"
          class="grid size-6 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </header>
      <div class="space-y-3 text-xs">
        <label class="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2">
          <span>Assistente habilitado</span>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => setAiAssistantEnabled(v)}
          />
        </label>
        <div>
          <label class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Acesso
          </label>
          <AnimatedTabs<"platform" | "byok" | "connected">
            ariaLabel="Modo de acesso"
            size="sm"
            className="w-full"
            items={[
              { id: "platform", label: "Plataforma" },
              { id: "byok", label: "Minha API key" },
              ...(provider === "gemini"
                ? [{
                    id: "connected" as const,
                    label: "Conta conectada",
                    disabled: !hasConnectedGemini,
                    title: !hasConnectedGemini
                      ? "Você precisa conectar uma conta Google primeiro"
                      : undefined
                  }]
                : [])
            ]}
            value={accessMode}
            onChange={(v) => setAiAccessMode(v)}
          />
        </div>
        <div>
          <label class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Provedor
          </label>
          <div class="grid grid-cols-2 gap-1 rounded-md border border-border bg-background p-0.5">
            <ByokProviderBtn
              label="Gemini"
              active={provider === "gemini"}
              onClick={() => setAiProvider("gemini")}
            />
            <ByokProviderBtn
              label="GPT / Codex"
              active={provider === "openai"}
              onClick={() => setAiProvider("openai")}
            />
            <ByokProviderBtn
              label="Claude"
              active={provider === "anthropic"}
              onClick={() => setAiProvider("anthropic")}
            />
            <ByokProviderBtn
              label="Grok"
              active={provider === "xai"}
              onClick={() => setAiProvider("xai")}
            />
            <ByokProviderBtn
              label="DeepSeek"
              active={provider === "deepseek"}
              onClick={() => setAiProvider("deepseek")}
            />
          </div>
        </div>
        <div>
          <label class="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Sparkles className="size-3" />
            Modelo opcional
          </label>
          <input
            type="text"
            value={model}
            placeholder={
              provider === "openai"
                ? "Ex.: gpt-5-mini ou gpt-5.2-codex"
                : "Deixe vazio para usar o padrão"
            }
            onInput={(e) => setAiModel((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30"
          />
          <div class="mt-2 flex flex-wrap gap-1">
            {providerCatalog.models.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setAiModelPreset(item.id, item.value)}
                class={`rounded-md border px-2 py-1 text-[10px] transition-colors ${
                  model === item.value && uiPrefs.value.aiModelCatalogId === item.id
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-background hover:bg-accent"
                }`}
                title={item.description}
              >
                {item.label}
              </button>
            ))}
          </div>
          <p class="mt-1 text-[10px] text-muted-foreground">
            No provedor OpenAI, você pode usar um modelo GPT ou um modelo Codex
            quando quiser respostas mais técnicas.
          </p>
        </div>
        {accessMode === "byok" && (
          <div>
            <label class="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <Key className="size-3" />
              API Key{" "}
              {provider === "openai"
                ? "OpenAI"
                : provider === "anthropic"
                  ? "Claude"
                  : provider === "xai"
                    ? "Grok"
                    : provider === "deepseek"
                      ? "DeepSeek"
                      : "Gemini"}
            </label>
            <input
              type="password"
              value={apiKey}
              placeholder="Cole sua chave do provedor"
              onInput={(e) =>
                setAiByok(
                  provider,
                  (e.target as HTMLInputElement).value,
                  data.value?.user?.id ?? ""
                )
              }
              class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30"
            />
            <p class="mt-1 text-[10px] text-muted-foreground">
              Sua chave fica salva localmente no navegador (não vai pro
              servidor além do necessário pra fazer a chamada).
            </p>
          </div>
        )}
        <p class="text-[10px] text-muted-foreground">
          {provider === "gemini"
            ? hasConnectedGemini
              ? "Sua conta Google ja esta conectada para uso com Gemini."
              : "Conecte sua conta Google para liberar Conta conectada no Gemini. Para o aluno, o fluxo e so clicar em conectar e autorizar."
            : "Para este provedor, use Plataforma ou Minha API key."}
        </p>
      </div>
    </div>
  );
}

function ByokProviderBtn({
  label,
  active,
  onClick,
  disabled = false
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active ? "true" : "false"}
      class={`flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "bg-accent text-foreground shadow-sm"
          : disabled
            ? "cursor-not-allowed text-muted-foreground opacity-60"
            : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

// Banner que substitui o AiAssistantBody quando o tutor está aberto em
// janela dedicada. Mesma vibe do banner de anotações ("janela aberta"),
// com botão pra puxar de volta — fecha o popup e re-renderiza o body inline.
function AiPopupBannerInline({ bankId }: { bankId: string | null }) {
  function bringBack() {
    if (!bankId) return;
    // Foca a janela popup e pede pra ela se fechar via broadcast.
    try {
      const w = window.open("", `ai-assistant-${bankId}`);
      if (w) w.close();
    } catch {
      /* sem permissão pra fechar — o popup vai cair sozinho no F5/fechar do user */
    }
  }
  return (
    <div class="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-5 text-center">
      <div class="grid size-12 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/30">
        <PictureInPicture2 className="size-5" />
      </div>
      <div class="space-y-1">
        <p class="text-sm font-bold">Tutor IA aberto em outra janela</p>
        <p class="text-xs text-muted-foreground">
          O contexto da questão acompanha sua navegação aqui automaticamente.
        </p>
      </div>
      <button
        type="button"
        onClick={bringBack}
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
      >
        Trazer para cá
      </button>
    </div>
  );
}
